import { blue, green, orange, red } from "@ant-design/colors"
import { Alert, Button, Calendar, Card, ConfigProvider, Descriptions, Space, Spin, Tooltip } from "antd"
import { calendarCellClassNames } from "./calendarCellClassNames"
import classnames from "classnames"
import moment from "moment-timezone"
import React, { FC, useContext, useEffect, useRef, useState } from "react"
import { Redirect, useHistory, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
    CourseBookingBasePriceBlockingReason,
    CourseDetailsInput,
    CourseLateEntryPricingMode,
    GetCourseDetailsByIdPublicQuery,
    NewBookingInput,
    useGetCourseDetailsByIdPublicQuery,
    useSettingEndUserAppQuery,
} from "../../generated/graphql"
import Page from "../../layouts/Page/Page"
import { URL_BASE, URL_BOOKING_SUMMARY, URL_LOGIN, URL_RESULTS } from "../../models/url"
import { colorGrey } from "../../styles/colors"
import {
    renderCoursePrice,
    renderEntranceFee,
    renderFirstCourseLesson,
    renderFreePlaces,
    renderStatus,
    renderInstructors,
    renderAgeRange,
} from "../../views/TableColumnRenderers/TableColumnRenderers"
import css from "./CourseDetails.module.css"
import PanelCard from "../../views/PanelCard/PanelCard"
import { effectiveBookingRestriction } from "../../views/SelectAttendeeTable/checkAttendee"
import { useMediaSize } from "../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import { SettingsContext } from "../../layouts/Main/MainLayout"
import { clampTimeoutDelay } from "../../helpers/clampTimeoutDelay"
import { useCurrency } from "../../helpers/currencyHelper"
import { MemberIconSvg, PrerequisiteCoursesIconSvg } from "../../assets/icons/OtherIcons"
import PrerequisiteCourses from "./PrerequisiteCourses"
import { RestrictionsType, getBookingRestrictions } from "../common/helpers"
import { normalizeDiscountCodeInput } from "../CreateBooking/BookingSummary/BookingPricePreview/discountCodeMessages"
import { discountCodeForCourse } from "../CreateBooking/BookingSummary/BookingPricePreview/linkDiscountCode"
import { resolvePaymentTypeChoice } from "../CreateBooking/BookingSummary/BookingPricePreview/paymentTypeChoice"
import { safeStorage } from "../../helpers/safeStorage"
import { LinkOutlined } from "@ant-design/icons"
import ReactMarkdown from "react-markdown"
import MarkdownImage from "../../views/MarkdownImage/MarkdownImage"
import tinycolor from "tinycolor2"
import QueryErrorAlert from "../../components/QueryErrorAlert"

type CourseDetailsProps = { style?: React.CSSProperties }

// Nach so vielen Millisekunden im Dauer-Ladezustand wird unter dem Spinner ein Ausweg
// zurück zur Kurssuche angeboten (exportiert, damit Tests die Wartezeit kennen)
export const COURSE_DETAILS_LOADING_ESCAPE_DELAY_MS = 5000

const CourseDetails: FC<CourseDetailsProps> = ({ style }) => {
    const history = useHistory()
    const mediaSize = useMediaSize()
    const { media } = mediaSize
    const { t } = useTranslation()
    // antd-Präfix (iFrame: ant, Embed: koe) für die nachgebaute Kalenderzelle
    const { getPrefixCls } = useContext(ConfigProvider.ConfigContext)
    const pickerPrefix = getPrefixCls("picker")
    const [courseId, setCourseId] = useState<string>()
    const params = useParams<{ id?: string }>()
    const isLoggedIn = !!safeStorage.getItem("loggedInUser")
    const currency = useCurrency()

    useEffect(() => {
        if (params.id) {
            setCourseId(params.id)
        }
    }, [params])

    const {
        data: courseData,
        loading: loadingCourseData,
        error: errorCourseData,
        refetch: refetchCourseData,
    } = useGetCourseDetailsByIdPublicQuery({
        skip: !courseId,
        variables: { courseId: courseId ?? "" },
        fetchPolicy: "network-only",
    })

    const course: GetCourseDetailsByIdPublicQuery["courseByIdPublic"] | undefined = courseData?.courseByIdPublic
    const restrictions: RestrictionsType = getBookingRestrictions(course?.bookingRestriction)

    const { data: appSettingsData } = useSettingEndUserAppQuery()
    const memberDiscountText = appSettingsData?.settingEndUserAppPublic?.textEntries?.memberDiscountTitle ?? "Mitgliederpreis"

    // SAVE SELECTED COURSE TO LOCAL STORAGE
    useEffect(() => {
        if (course) {
            safeStorage.setItem("course", JSON.stringify(course))
        }
    }, [course])

    // Späteinstieg-Preis-Quote läuft zu `validUntil` ab (i. d. R. der nächste Lektionsstart).
    // Danach die Detail-Query neu laden, damit Preis/Buchbarkeit aktuell bleiben.
    // Loop-Schutz: Liegt validUntil in der Vergangenheit, wird höchstens EINMAL sofort
    // nachgeladen und danach ein Mindestabstand von 30 s eingehalten.
    const QUOTE_REFETCH_MIN_INTERVAL_MS = 30000
    const lastQuoteRefetchAtRef = useRef<number>(0)
    const quoteValidUntil = course?.bookingPriceQuote?.validUntil ?? null

    useEffect(() => {
        if (!quoteValidUntil) {
            return undefined
        }

        const runRefetch = () => {
            lastQuoteRefetchAtRef.current = Date.now()
            refetchCourseData()
        }

        const msUntilExpiry = new Date(quoteValidUntil).getTime() - Date.now()

        if (msUntilExpiry > 0) {
            // Int32-Clamp: validUntil > ~24,86 Tage würde den setTimeout-Delay
            // überlaufen lassen und (kalenderabhängig) sofort refetchen.
            const timeoutId = window.setTimeout(runRefetch, clampTimeoutDelay(msUntilExpiry))
            return () => window.clearTimeout(timeoutId)
        }

        // Bereits abgelaufen: sofort nachladen, aber frühestens 30 s nach dem letzten Refetch.
        const sinceLastRefetch = Date.now() - lastQuoteRefetchAtRef.current
        if (sinceLastRefetch >= QUOTE_REFETCH_MIN_INTERVAL_MS) {
            runRefetch()
            return undefined
        }

        const timeoutId = window.setTimeout(runRefetch, QUOTE_REFETCH_MIN_INTERVAL_MS - sinceLastRefetch)
        return () => window.clearTimeout(timeoutId)
    }, [quoteValidUntil, refetchCourseData])

    // Kurs nicht gefunden: Query ist abgeschlossen (nicht loading, kein Fehler), aber das
    // Backend hat keinen Kurs geliefert — z.B. abgelaufene oder falsche courseId in der URL.
    const courseNotFound = Boolean(courseId) && !loadingCourseData && !errorCourseData && !course

    // Veralteten Kurs aus dem Storage räumen — als Effekt, nicht als Render-Seiteneffekt
    useEffect(() => {
        if (courseNotFound) {
            safeStorage.removeItem("course")
        }
    }, [courseNotFound])

    // Fehler beim Laden: Query abgeschlossen, aber Apollo meldet einen Error
    const showLoadError = Boolean(errorCourseData) && !loadingCourseData

    // Dauer-Ladezustand (hängende Query oder dauerhaft fehlende courseId): Der User soll
    // nicht endlos im Spinner festsitzen — nach kurzer Wartezeit erscheint ein Ausweg.
    const isStuckLoading = (!courseId || !course) && !showLoadError && !courseNotFound
    const [showLoadingEscape, setShowLoadingEscape] = useState(false)

    useEffect(() => {
        if (!isStuckLoading) {
            // Spin-Zustand verlassen: Ausweg zurücksetzen, damit er beim nächsten
            // schnellen Ladevorgang nicht sofort aufblitzt
            setShowLoadingEscape(false)
            return
        }
        const timeoutId = window.setTimeout(() => setShowLoadingEscape(true), COURSE_DETAILS_LOADING_ESCAPE_DELAY_MS)
        return () => window.clearTimeout(timeoutId)
    }, [isStuckLoading])

    // CUSTOMIZABLE DETAILS -------------------------------------------------------------------------------------------

    // lessonCount kam erst mit Backend-PR ko-X-main #615 ins Schema; der
    // Settings-Payload ist ein JSON-Blob, daher hier lokal typisiert, bis der
    // nächste reguläre Codegen den generierten CourseDetailsInput nachzieht.
    type CourseCustomDetails = CourseDetailsInput & {
        lessonCount?: { label?: string; enabled?: boolean }
    }

    const defaultCourseCustomDetails: CourseCustomDetails = {
        age: { label: "Alter", enabled: false },
        calendar: { label: "Kurskalender", enabled: false },
        courseLessons: { label: "Kurseinheiten", enabled: true },
        lessonCount: { label: "Anzahl Kurseinheiten", enabled: false },
        city: { label: "Ort", enabled: false },
        courseFee: { label: "Kursgebühr", enabled: false },
        courseInfos: { label: "Informationen zum Kurs", enabled: false },
        entranceFee: { label: "Badeintritt/Einheit", enabled: false },
        freePlaces: { label: "Freie Plätze", enabled: false },
        status: { label: "Status", enabled: false },
        location: { label: "Kursort", enabled: false },
        instructor: { label: "Kursleiter", enabled: true },
        firstCourseLesson: { label: "Kursbeginn", enabled: true },
    }

    const [customCourseDetails, setCustomCourseDetails] = useState<CourseCustomDetails>(defaultCourseCustomDetails)

    const fetchedCourseCustomDetails: CourseCustomDetails = useContext(SettingsContext).courseDetails

    // console.log("fetchedCourseCustomDetails: ", fetchedCourseCustomDetails)

    useEffect(() => {
        if (fetchedCourseCustomDetails) {
            setCustomCourseDetails(fetchedCourseCustomDetails)
        }
    }, [customCourseDetails, fetchedCourseCustomDetails])

    const showCourseInfoBox =
        customCourseDetails.age?.enabled === true ||
        customCourseDetails.lessonCount?.enabled === true ||
        customCourseDetails.courseFee?.enabled === true ||
        customCourseDetails.entranceFee?.enabled === true ||
        customCourseDetails.freePlaces?.enabled === true ||
        customCourseDetails.location?.enabled === true ||
        customCourseDetails.instructor?.enabled === true ||
        customCourseDetails.firstCourseLesson?.enabled === true ||
        customCourseDetails.city?.enabled === true

    // CUSTOMIZABLE DETAILS -------------------------------------------------------------------------------------------

    if (!courseId || !course) {
        // Fehler beim Laden: verständliche Meldung mit Retry statt ignoriertem Apollo-Error.
        // Retry-Feedback (Button-Loading) und Rejection-Handling übernimmt QueryErrorAlert.
        if (showLoadError) {
            return <QueryErrorAlert testId='course-details-error' message={t("courseDetails.loadErrorMessage")} onRetry={refetchCourseData} />
        }

        // Kurs nicht gefunden: deklarativ umleiten statt history.push als Render-Seiteneffekt
        // (verhindert Flickering und Mehrfach-Render-Pfade)
        if (courseNotFound) {
            return <Redirect to={URL_RESULTS.replace(":resultType", "courseNotFound")} />
        }

        // courseId wird noch aus den URL-Params übernommen oder die Query lädt.
        // Hängt der Ladezustand zu lange, erscheint zusätzlich ein Ausweg zur Kurssuche.
        return (
            <div data-testid='course-details-loading' style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 15, padding: "50px 0" }}>
                <Spin />
                {showLoadingEscape && (
                    <Button data-testid='course-details-loading-escape' type='link' onClick={() => history.push(URL_BASE)}>
                        {t("resultScreen.backToSearch", "Zurück zur Kurssuche")}
                    </Button>
                )}
            </div>
        )
    }

    type ContactCardCSSProperties = React.CSSProperties & {
        "--column-count": number
    }

    const actualStyle: ContactCardCSSProperties = {
        "--column-count": 1,
        lineBreak: "auto",
        // ...style,
    }

    const firstCourseLessonOfMonth: Array<string> = []

    const courseLessonDates = course.lessons.map((lesson) => {
        return moment(lesson.startDateTime).format("YYYY.MM.DD")
    })

    // Datums-Strings der bereits vergangenen Kurstermine (Kalender-Dimmen, reine Anzeige).
    const pastLessonDates = new Set(
        course.lessons.filter((lesson) => isLessonInPast(lesson.startDateTime)).map((lesson) => moment(lesson.startDateTime).format("YYYY.MM.DD")),
    )

    // Repliziert die antd-Standard-Kalenderzelle (fullscreen=false), damit unmarkierte
    // Tage pixelidentisch bleiben; nur vergangene Kurstermine werden zusätzlich gedimmt.
    const renderCalendarCell = (date: moment.Moment) => {
        const isToday = moment(date).isSame(moment(), "day")
        const cellClass = calendarCellClassNames(pickerPrefix, isToday)
        const inner = (
            <div className={cellClass.inner}>
                <div className={cellClass.value}>{String(date.date()).padStart(2, "0")}</div>
                <div className={cellClass.content} />
            </div>
        )
        if (!pastLessonDates.has(date.format("YYYY.MM.DD"))) {
            return inner
        }
        return (
            <Tooltip title={t("lateEntry.lessonInPast", "bereits stattgefunden")}>
                <div className={css.calendarLessonInPast} data-testid='calendar-cell-past'>
                    {inner}
                </div>
            </Tooltip>
        )
    }

    const months = () => {
        const months: Array<string> = []
        let nthMonth = 0

        course.lessons.forEach((lesson, index) => {
            const firstDate = moment(lesson.startDateTime).startOf("month").format("YYYY-MM-DD hh:mm")
            if (!months.includes(firstDate)) {
                nthMonth = nthMonth + 1
                months.push(firstDate)
            }
            if (nthMonth > firstCourseLessonOfMonth.length) {
                firstCourseLessonOfMonth.push(lesson.startDateTime)
            }
        })

        return months
    }

    const bookingSummaryLink = URL_BOOKING_SUMMARY.replace(":courseId", courseId)

    const updateLocalStorageBookingData = (course: GetCourseDetailsByIdPublicQuery["courseByIdPublic"]) => {
        const bookingData = JSON.parse(safeStorage.getItem("bookingData") as string)

        const newBookingData: NewBookingInput = {
            courseId: course.id,
            coursePriceId: course.coursePrice[0].id,
            grossPrice: course.coursePrice[0].grossPrice,
            netPrice: course.coursePrice[0].netPrice,
            vat: course.coursePrice[0].vat,
            notifyUser: true,
            startDateTime: course.startDateTime,
            agbConfirmed: false,
            privacyStatementConfirmed: false,
            additionalInfo: null,
            // Zahlweise ist kursgebunden wie der Rabattcode: bei einem anderen Kurs gilt dessen Standard.
            paymentType: bookingData?.courseId === course.id ? resolvePaymentTypeChoice(bookingData?.paymentType, course) : course.paymentType,
            discountCode: normalizeDiscountCodeInput(discountCodeForCourse(course.id, bookingData)) || null,
            // Rechnungsadresse gilt je Buchung: bei einem anderen Kurs erst wieder auf Wunsch (Felder bleiben zum erneuten Anhaken).
            hasBillingAddress: bookingData?.courseId === course.id ? bookingData?.hasBillingAddress ?? false : false,
        }

        if (!bookingData) {
            safeStorage.setItem("bookingData", JSON.stringify(newBookingData))
        } else {
            const updatedBookingData = { ...bookingData, ...newBookingData }
            safeStorage.setItem("bookingData", JSON.stringify(updatedBookingData))
        }
    }

    const goToBookingSummary = () => {
        if (isLoggedIn) {
            updateLocalStorageBookingData(course)
            history.push(bookingSummaryLink)
        } else {
            history.push({
                pathname: URL_LOGIN,
                state: { fromUrl: history.location.pathname }, // Passing the current location
            })
        }
    }

    const descriptionStyle = {
        bordered: false,
        contentStyle: { paddingLeft: 10, display: "flex", justifyContent: "space-between" },
        labelStyle: { paddingLeft: 10, fontWeight: 600 },
    }

    type DetailCardProps = {
        columns?: number
        title?: string | React.ReactNode
        gapAfter?: boolean
        style?: React.CSSProperties
        children?: React.ReactNode
        visible?: boolean
        className?: string
    }

    const DetailCard: FC<DetailCardProps> = ({ columns, title, gapAfter, style, children, visible = true, className }) => {
        if (!visible) {
            return null
        }

        return (
            <PanelCard columns={columns} title={title} gapAfter style={style} className={className}>
                {children}
            </PanelCard>
        )
    }

    const mediaColumns = media === "xxl" ? 4 : media === "xl" ? 3 : media === "md" ? 2 : media === "sm" ? 2 : 1

    const courseTypeColor = course.courseType.color

    type CoursePlaceStatus = "freePlaces" | "waitlist" | "full" | "blocked" | "notBookable"

    // Späteinstieg-Sperre: Meldet die Server-Quote den Kurs als nicht buchbar, wird
    // fail-closed sowohl Buchen als auch Warteliste gesperrt (Vorrang vor freePlaces/
    // Warteliste, aber nach blockBookingOnline). Fehlt die Quote, bleibt alles wie bisher.
    const lateEntryBlockingReason = (): string | null => {
        const quote = course.bookingPriceQuote
        if (!quote || quote.isBookable !== false) {
            return null
        }
        if (quote.blockingReason === CourseBookingBasePriceBlockingReason.NoEligibleLessons) {
            return t("lateEntry.notBookableCourseFinished", "Dieser Kurs ist bereits abgeschlossen und kann nicht mehr gebucht werden.")
        }
        if (quote.blockingReason === CourseBookingBasePriceBlockingReason.MinimumPartialBookingLessonCountNotReached) {
            return t("lateEntry.notBookableTooFewLessons", "Für diesen Kurs sind nicht mehr genügend Kurseinheiten verfügbar.")
        }
        return t("lateEntry.notBookableButton", "Nicht buchbar")
    }

    // Hinweis "voller Kurspreis trotz begonnener Einheiten": nur wenn der Kurs im
    // Original-Preis-Modus läuft, bereits Einheiten stattfanden (eligible < total),
    // keine Reduktion greift und der Kurs weiterhin buchbar ist.
    const quote = course.bookingPriceQuote
    const showFullPriceNotice = Boolean(
        quote &&
            quote.isBookable &&
            quote.reduction === 0 &&
            quote.effectiveMode === CourseLateEntryPricingMode.OriginalPrice &&
            quote.eligibleLessonCount < quote.totalLessonCount
    )

    const coursePlaceStatus = (): CoursePlaceStatus => {
        const waitListHasMax = course?.maxWaitlist !== null && course.maxWaitlist !== undefined
        const remainingWaitlistPlaces = course.maxWaitlist ?? 0 - course.waitListCount
        const bookingIsBlocked = course.blockBookingOnline !== false

        if (bookingIsBlocked) {
            return "blocked"
        }

        if (course.bookingPriceQuote && course.bookingPriceQuote.isBookable === false) {
            return "notBookable"
        }

        if (course.freePlaces > 0) {
            return "freePlaces"
        } else if (waitListHasMax && remainingWaitlistPlaces <= 0) {
            return "full"
        }

        return "waitlist"
    }

    // Von einer aktiven Buchung im Konto verlinkt: "Jetzt buchen" wäre verwirrend,
    // der Kurs ist ja schon gebucht — der Button wirbt dann für weitere Teilnehmer
    const fromActiveBooking = (history.location.state as { fromActiveBooking?: boolean } | undefined)?.fromActiveBooking === true
    const bookNowLabel = fromActiveBooking ? "Weiteren Teilnehmer buchen" : "Jetzt buchen"

    const buttonText = () => {
        switch (coursePlaceStatus()) {
            case "blocked":
                return "Nicht buchbar"
            case "notBookable":
                return t("lateEntry.notBookableButton", "Nicht buchbar")
            case "freePlaces":
                return bookNowLabel
            case "full":
                return "Ausgebucht"
            case "waitlist":
                return bookNowLabel
            default:
                return bookNowLabel
        }
    }

    const getTextColor = (color?: string): string => (color !== undefined && tinycolor(color).isLight() ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.8)")

    const getDarkenedColor = (color: string): string => (color !== undefined && tinycolor(color).isLight() ? tinycolor(color).darken(15).toString() : tinycolor(color).darken(10).toString())

    const getBorderColor = (color: string, darken?: boolean): string =>
        color !== undefined && tinycolor(color).isLight() && darken ? tinycolor(color).darken(40).toString() : tinycolor(color).lighten(30).toString()

    const isLight = tinycolor(courseTypeColor).isLight()

    const buttonStyle = (darken: boolean): React.CSSProperties => {
        const textColor = getTextColor(courseTypeColor)
        const borderColor = getBorderColor(courseTypeColor, darken)
        const baseStyle = {
            fontWeight: 600,
            border: `1px solid`,
            borderColor: borderColor,
            borderRadius: 3,
            color: textColor,
        }

        const darkenedColor = getDarkenedColor(courseTypeColor)
        const waitListStyle = {
            backgroundColor: darken ? darkenedColor : courseTypeColor,
            // borderColor: darken ? darkenedColor : courseTypeColor,
        }
        const freePlacesStyle = {
            backgroundColor: darken ? darkenedColor : courseTypeColor,
            // borderColor: darken ? darkenedColor : courseTypeColor,
        }
        switch (coursePlaceStatus()) {
            case "freePlaces":
                return { ...baseStyle, ...freePlacesStyle }
            case "full":
                return { ...baseStyle }
            case "waitlist":
                return { ...baseStyle, ...waitListStyle }
            default:
                return { ...baseStyle, ...waitListStyle }
        }
    }

    const renderBookingButton = (darken: boolean, style?: React.CSSProperties) => {
        return (
            <Button
                data-testid='booking-button-course-details'
                style={{ ...buttonStyle(darken), ...style }}
                type='primary'
                // onClick={() => goToBookingSummary()}
                onClick={() => {
                    if (coursePlaceStatus() === "blocked" || coursePlaceStatus() === "notBookable") {
                        return
                    }
                    goToBookingSummary()
                }}
                disabled={coursePlaceStatus() === "full" || coursePlaceStatus() === "blocked" || coursePlaceStatus() === "notBookable"}
            >
                {buttonText()}
            </Button>
        )
    }

    const detailsCardTitle = () => {
        const iconSize = 16
        const entryStyle: React.CSSProperties = {
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
            // backgroundColor: orange[1],
            opacity: 0.8,
            padding: "3px 10px 3px 7px",
            height: iconSize + 6,
            borderRadius: iconSize - 4,
            // marginTop: -3,
        }

        const membersOnly = restrictions.membersOnly ? (
            <Space size={3} style={{ ...entryStyle, color: green[8], backgroundColor: green[1], fontWeight: 500 }}>
                <MemberIconSvg color={green[6]} size={23} style={{ paddingBottom: 2 }} />
                <span>{"Nur für Mitglieder buchbar"}</span>
            </Space>
        ) : null
        const prerequisiteCourses = restrictions.prerequisiteCourses ? (
            <Space size={3} style={{ ...entryStyle, color: orange[8], backgroundColor: orange[1], fontWeight: 500 }}>
                <PrerequisiteCoursesIconSvg color={orange[6]} size={20} style={{ marginTop: 7 }} />

                <span>{"Voraussetzungskurse"}</span>
            </Space>
        ) : null

        const renderIcons = () => {
            if (membersOnly && prerequisiteCourses) {
                return (
                    <Space size={3}>
                        {membersOnly}
                        {prerequisiteCourses}
                    </Space>
                )
            }
            if (membersOnly) {
                return membersOnly
            }
            if (prerequisiteCourses) {
                return prerequisiteCourses
            }

            return null
        }

        return (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>{"Kursdaten"}</div>
                {renderIcons()}
            </div>
        )
    }

    const processMarkdown = (input: string) => {
        return input.replace(/(.*)\n---/g, "$1\n\n---\n\n")
    }

    return (
        <Page
            data-testid='course-details'
            style={actualStyle}
            content={
                <>
                    <Card
                        title={
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    // Kontrastfarbe statt fixem Dunkelgrau — bei dunklen Kursart-Farben
                                    // (z.B. Schwarz) war der Titel sonst unlesbar
                                    color: getTextColor(courseTypeColor),
                                }}
                                className={css.titles}
                            >
                                <Space className={css.titlePrimary}>
                                    <h1
                                        style={{
                                            marginBottom: 0,
                                            paddingBottom: 0,
                                            color: getTextColor(courseTypeColor),
                                        }}
                                    >
                                        Kursdetails
                                    </h1>
                                    {renderBookingButton(true)}
                                </Space>
                                <Space className={css.titleSecondary}>
                                    <div>{course.courseType.name}</div>
                                    <div>{course.prefixedCourseNumber}</div>
                                </Space>
                            </div>
                        }
                        // actions={[renderBookingButton(false, { width: `calc(100% - 20px)`, height: 42, fontSize: 16 })]}
                        actions={[
                            renderBookingButton(isLight ? true : false, {
                                width: `calc(100% - 20px)`,
                                height: 42,
                                fontSize: 16,
                                color: getTextColor(courseTypeColor),
                            }),
                        ]}
                        key={course.id}
                        bordered={false}
                        className={css.card}
                        size='small'
                        headStyle={{
                            background: courseTypeColor,
                        }}
                    >
                        {coursePlaceStatus() === "notBookable" && lateEntryBlockingReason() && (
                            <div
                                data-testid='late-entry-blocked-notice'
                                style={{ margin: "10px 12px", padding: "8px 12px", borderRadius: 4, background: red[1], color: red[7], fontWeight: 500 }}
                            >
                                {lateEntryBlockingReason()}
                            </div>
                        )}
                        {showFullPriceNotice && (
                            <div
                                data-testid='late-entry-full-price-notice'
                                style={{ margin: "10px 12px", color: colorGrey, fontSize: 13, lineHeight: 1.3 }}
                            >
                                {t("lateEntry.fullPriceNotice", "Für diesen Kurs gilt der volle Kurspreis, auch wenn bereits Einheiten stattgefunden haben.")}
                            </div>
                        )}
                        <DetailCard columns={1} title={detailsCardTitle()} gapAfter style={{ minWidth: 1, background: "white" }} visible={showCourseInfoBox}>
                            <Descriptions
                                bordered={media === "xs" ? true : false}
                                size={media === "xs" ? "small" : "default"}
                                contentStyle={descriptionStyle.contentStyle}
                                labelStyle={descriptionStyle.labelStyle}
                                className={css.descriptions}
                                column={{ xs: 1, sm: 1, md: 2, xl: 2 }}
                            >
                                {/* {customCourseDetails.city?.enabled && (
                                    <Descriptions.Item label='Ort'>{course.location.city}</Descriptions.Item> // here!
                                )} */}
                                {customCourseDetails.city?.enabled && course.location.website ? (
                                    <Descriptions.Item label='Kursort'>
                                        <Button size='small' type='link'>
                                            <a href={course.location.website} target='_blank' rel='noopener noreferrer'>
                                                {course.location.name}
                                                <LinkOutlined
                                                    style={{
                                                        color: blue[5],
                                                        marginLeft: 5 /* , fontSize: "small" */,
                                                    }}
                                                />
                                            </a>
                                        </Button>
                                    </Descriptions.Item>
                                ) : (
                                    customCourseDetails.city?.enabled && <Descriptions.Item label='Kursort'>{course.location.name}</Descriptions.Item>
                                )}
                                {customCourseDetails.firstCourseLesson?.enabled && (
                                    <Descriptions.Item label='Kursbeginn'>{renderFirstCourseLesson(course.startDateTime, course.endDateTimeFirstCourseLesson)}</Descriptions.Item>
                                )}
                                {customCourseDetails.lessonCount?.enabled === true && (
                                    <Descriptions.Item label='Anzahl Kurseinheiten'>{course.lessons.length}</Descriptions.Item>
                                )}
                                {customCourseDetails.instructor?.enabled && <Descriptions.Item label='Kursleiter'>{renderInstructors(course.instructors)}</Descriptions.Item>}
                                {customCourseDetails.freePlaces?.enabled && <Descriptions.Item label='Freie Plätze'>{renderFreePlaces(course.freePlaces, course.maxAttendees)}</Descriptions.Item>}
                                {customCourseDetails.status?.enabled && <Descriptions.Item label='Status'>{renderStatus(course.freePlaces, course.maxAttendees)}</Descriptions.Item>}
                                {customCourseDetails.age?.enabled && (
                                    <Descriptions.Item label='Alter'>{renderAgeRange(course.courseType.minAge, course.courseType.maxAge)}</Descriptions.Item>
                                )}
                                {customCourseDetails.courseFee?.enabled && (
                                    <Descriptions.Item label='Kursgebühr'>{renderCoursePrice(course, memberDiscountText, mediaSize, currency, true)}</Descriptions.Item>
                                )}

                                {customCourseDetails.entranceFee?.enabled && (
                                    <Descriptions.Item label='Badeintritt inklusive'>{renderEntranceFee(course.coursePrice?.[0].includedEntranceFee, mediaSize, currency)}</Descriptions.Item>
                                )}
                            </Descriptions>
                        </DetailCard>
                        {restrictions.prerequisiteCourses && (
                            <DetailCard
                                columns={mediaColumns}
                                title={"Voraussetzungskurse für diesen Kurs"}
                                style={{ minWidth: 1, background: "white" }}
                                visible={customCourseDetails.calendar?.enabled}
                            >
                                <PrerequisiteCourses bookingRestriction={course.bookingRestriction} mediaColumns={mediaColumns} />
                                {effectiveBookingRestriction(course)?.allowWaitlistWithoutPrerequisite && (
                                    <Alert
                                        type='info'
                                        showIcon
                                        data-testid='course-details-prerequisite-waitlist-hint'
                                        style={{ marginTop: 8 }}
                                        message={t("courseDetails.prerequisiteWaitlistHint", "Ohne den Voraussetzungskurs ist nur ein Eintrag auf die Warteliste möglich.")}
                                    />
                                )}
                            </DetailCard>
                        )}
                        <DetailCard columns={1} gapAfter title={customCourseDetails.courseInfos?.label} style={{ minWidth: 1, background: "white" }} visible={customCourseDetails.courseInfos?.enabled}>
                            <div className={classnames(css.container)} style={actualStyle}>
                                {course.websiteInfo && course.websiteInfo.length > 0 ? (
                                    <ReactMarkdown skipHtml={false} className={css.markdown} components={{ img: MarkdownImage }}>
                                        {processMarkdown(course.websiteInfo)}
                                    </ReactMarkdown>
                                ) : (
                                    <div className={classnames(css.entry, css.markdown)} style={{ color: colorGrey }}>
                                        Für diesen Kurs liegen keine Infos vor.
                                    </div>
                                )}
                            </div>
                        </DetailCard>
                        {/* <DetailCard
                            columns={1}
                            gapAfter
                            // title='Informationen zum Kurs'
                            title={customCourseDetails.courseInfos?.label}
                            style={{ minWidth: 1, background: "white" }}
                            visible={customCourseDetails.courseInfos?.enabled}
                        >
                            <div className={classnames(css.container)} style={actualStyle}>
                                {course.websiteInfo && course.websiteInfo.length > 0 ? (
                                    <div className={css.entry}>{course.websiteInfo}</div>
                                ) : (
                                    <div className={css.entry} style={{ color: colorGrey }}>
                                        Für diesen Kurs liegen keine Infos vor.
                                    </div>
                                )}
                            </div>
                        </DetailCard> */}
                        <DetailCard
                            columns={mediaColumns}
                            title={customCourseDetails.courseLessons?.label}
                            style={{ minWidth: 1, background: "white" }}
                            visible={customCourseDetails.courseLessons?.enabled === true}
                        >
                            <RenderLessons record={course} />
                        </DetailCard>
                        <DetailCard
                            columns={mediaColumns}
                            // title={`Termine`}
                            title={customCourseDetails.calendar?.label}
                            style={{ minWidth: 1, background: "white" }}
                            visible={customCourseDetails.calendar?.enabled}
                        >
                            {months().map((month, nthMonth) => {
                                return (
                                    <Calendar
                                        key={`${month}${nthMonth}`}
                                        className={css.calendar}
                                        fullscreen={false}
                                        defaultValue={moment(firstCourseLessonOfMonth[nthMonth])}
                                        headerRender={({ value, type, onChange, onTypeChange }) => {
                                            const currentMonth = moment(month).format("MMMM")

                                            return <h3 className={css.headLine}>{currentMonth}</h3>
                                        }}
                                        disabledDate={(date) => !courseLessonDates.includes(moment(date).format("YYYY.MM.DD")) || moment(month).format("M") !== moment(date).format("M")}
                                        dateFullCellRender={renderCalendarCell}
                                    />
                                )
                            })}
                        </DetailCard>
                    </Card>
                </>
            }
        ></Page>
    )
}

export default CourseDetails

type RenderLessonProps = {
    record: GetCourseDetailsByIdPublicQuery["courseByIdPublic"]
}

// Reine Anzeige-Logik: liegt der Termin vor JETZT (Client-Uhr)? Keinerlei Preislogik.
const isLessonInPast = (startDateTime: string): boolean => new Date(startDateTime).getTime() < Date.now()

// Exportiert, damit Tests die Kurseinheiten-Kacheln isoliert prüfen können.
export const RenderLessons: FC<RenderLessonProps> = ({ record }) => {
    const { t } = useTranslation()
    const { lessons, bookingCount, maxAttendees } = record

    const freePlaces = maxAttendees - bookingCount

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const color = freePlaces && freePlaces > 0 ? (freePlaces > 3 ? green[3] : orange[3]) : red[3]
    const sortedLessons = [...lessons].sort((a, b) => {
        return new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
    })

    return (
        <div className={css.courseLessonsContainer} style={{ margin: "-10px 0" }}>
            {sortedLessons.map((lesson, index) => {
                const isPast = isLessonInPast(lesson.startDateTime)
                const pastClass = isPast ? ` ${css.lessonInPast}` : ""
                const pastTitle = isPast ? t("lateEntry.lessonInPast", "bereits stattgefunden") : ""
                return (
                    <React.Fragment key={lesson.id ?? index}>
                        <Tooltip title={pastTitle}>
                            <div
                                className={`${css.courseLessonSmall}${pastClass}`}
                                data-testid={isPast ? "lesson-tile-past" : undefined}
                            >
                                {/* <div className={css.spacer} /> */}
                                <div className={css.lessonNum}>{index + 1}</div>
                                <div className={css.weekDay}>{`${moment(lesson.startDateTime).locale("dddd").format("dd")}`}</div>
                                <div className={css.date}>{`${moment(lesson.startDateTime).format("DD.MM.YY")}`}</div>
                                <div className={css.courseLessonTime}>{`${moment(lesson.startDateTime).format("LT")} - ${moment(lesson.endDateTime).format("LT")} Uhr`}</div>
                                <div className={css.spacer} />
                            </div>
                        </Tooltip>
                        <Tooltip title={pastTitle}>
                            <div
                                className={`${css.courseLessonLarge}${pastClass}`}
                                data-testid={isPast ? "lesson-tile-past" : undefined}
                            >
                                <div className={css.month}>{`${moment(lesson.startDateTime).locale("dddd").format("MMMM")}`}</div>
                                <div className={css.day}>{`${moment(lesson.startDateTime).locale("dddd").format("D")}`}</div>
                                <div className={css.weekday}>{`${moment(lesson.startDateTime).locale("dddd").format("dddd")}`}</div>
                                <div className={css.time}>{`${moment(lesson.startDateTime).format("LT")} - ${moment(lesson.endDateTime).format("LT")}`}</div>
                            </div>
                        </Tooltip>
                    </React.Fragment>
                )
            })}
        </div>
    )
}
